/**
 * «Bekreftet med Vipps» in the panel — the pieces every view that confirms a record with Vipps shares (owner 2026-10-29;
 * the server's vipps-signature.js, VIPPS_SIGNATURE.md). Written once for Utlegg and moved here when cash gifts, month locks
 * and board minutes were connected, so the four views cannot drift apart.
 *
 * The flow: a button POSTs /signatures/start with the panel's own address as the way back (the server allows nothing
 * else) and the page goes to Vipps; Vipps comes back to #/<view>?sig=<one-time code>; the view spends the code with the
 * person's own session (POST /signatures/finish) — the code alone confirms nothing — after taking it out of the address.
 * Every text says «bekreftet», never «signatur»: a Vipps Login is identification bound to the record's content, not a
 * signature.
 */
import React, { useEffect } from 'react';
import { errText, post, type Body } from '../lib/api';
import { Tag } from './ui';

/** Where Vipps sends this page back to: the panel itself (origin, path and query — the server allows only its own panel). */
export const panelReturn = () => `${location.origin}${location.pathname}${location.search}`;
/** The one-time code or the error in the address after a return from Vipps: #/utlegg?sig=… | ?sigError=… */
export function returned(hash: string): { sig: string | null; error: string | null } {
  const q = new URLSearchParams((hash.split('?')[1]) || '');
  return { sig: q.get('sig'), error: q.get('sigError') };
}
export const SIG_ERROR: Record<string, string> = {
  cancelled: 'Bekreftelsen med Vipps ble avbrutt.', expired: 'Bekreftelsen med Vipps tok for lang tid. Prøv igjen.',
  failed: 'Vipps kunne ikke bekrefte deg. Prøv igjen.', no_name: 'Vipps oppga ikke navnet ditt. Prøv igjen.',
  stale_login: 'Innloggingen i Vipps var ikke ny nok. Prøv igjen, og logg inn i Vipps på nytt.',
};
/** The server's refusals of a finished confirmation that the person must understand (security fix AUTH-1/AZ-1, 2026-10-29). */
export const FINISH_ERROR: Record<string, string> = {
  same_person: 'Den samme personen i Vipps har allerede bekreftet denne saken i en annen rolle. En annen person må bekrefte.',
  vipps_person_mismatch: 'Denne brukeren er knyttet til en annen person i Vipps. Bekreft med din egen Vipps, eller be driftsansvarlig nullstille koblingen.',
};

/** The record kinds the server knows (packages/core/vipps-signature.js KINDS). */
export type ConfirmKind = 'expense_claim' | 'cash_gift' | 'cash_gift_period_lock' | 'board_minutes';

/** Start a confirmation and go to Vipps. Returns an error text, or nothing (the page leaves). */
export async function startConfirm(subjectType: ConfirmKind, subjectId: string, role: string): Promise<string | null> {
  const r = await post('/signatures/start', { subjectType, subjectId, role, returnTo: panelReturn() });
  if (!r.ok) return r.body.message || errText(r.body);
  location.assign(r.body.authorizeUrl);
  return null;
}

/**
 * Back from Vipps on `route` (e.g. '/utlegg'): the code leaves the address first, then it is spent with this session.
 * `onFinished` gets the server's answer ({ subjectType, subjectId, role, confirmation }); `say` tells the person what
 * went wrong. Runs once, when the view opens.
 */
export function useVippsReturn(route: string, say: (text: string, tone: 'ok' | 'err') => void, onFinished: (body: Body) => void) {
  useEffect(() => {
    const { sig, error: sigError } = returned(location.hash);
    if (!sig && !sigError) return;
    history.replaceState(null, '', `${location.pathname}${location.search}#${route}`);
    if (sigError) { say(SIG_ERROR[sigError] || 'Bekreftelsen med Vipps gikk ikke.', 'err'); return; }
    void (async () => {
      const r = await post('/signatures/finish', { handoff: sig });
      if (!r.ok) { say(FINISH_ERROR[r.body.code] || r.body.message || errText(r.body), 'err'); return; }
      onFinished(r.body);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * One confirmation as a table row: «Bekreftet med Vipps · navn · tlf · tid · ref …», green while it counts, grey once the
 * record changed (`changedText` says what changed). A Vipps name that differs from the account's is a yellow warning for
 * whoever relies on it (`approver`).
 */
export function Stamp({ label, s, approver, changedText }: { label: string; s: Body | null; approver: boolean; changedText: string }) {
  if (!s) return <tr><th scope="row">{label}</th><td><span className="note">Ikke bekreftet med Vipps</span></td></tr>;
  return (
    <tr><th scope="row">{label}</th><td>
      <Tag tone={s.valid ? 'green' : 'grey'}>{s.valid ? 'Gjelder' : 'Gjelder ikke lenger'}</Tag> {s.line}{s.sealed ? ' · forseglet' : ''}
      {!s.valid && <div className="note">{changedText}</div>}
      {approver && s.nameMismatch && <div className="msg warn" role="note" style={{ background: '#fff3cd', color: '#5c4500', padding: '4px 8px', borderRadius: 6 }}>{s.mismatchText}</div>}
    </td></tr>
  );
}

/** Earlier confirmations that no longer count (replaced, or the record changed), folded away. */
export function VoidedStamps({ voided, changedWord }: { voided: Body[] | undefined; changedWord: string }) {
  if (!voided || !voided.length) return null;
  return (
    <details><summary className="note">Tidligere bekreftelser ({voided.length})</summary>
      <ul className="note">{voided.map((v: Body) => <li key={v.id}><Tag tone="grey">{v.voidedReason === 'replaced' ? 'erstattet' : changedWord}</Tag> {v.label_no}: {v.line}</li>)}</ul></details>
  );
}
